import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ============================================
// DATE/TIME UTILITIES - USE DEVICE LOCAL TIME
// ============================================
// Instead of trying to convert to IST timezone which has been problematic,
// we now use the device's local time directly. When an admin/user logs in
// from their laptop/phone, the device already knows its timezone.
// This is more reliable and works regardless of where the server is hosted.

/**
 * Format date using DEVICE'S LOCAL TIME
 * The user's device knows its timezone - no need to specify IST
 */
export function formatDateLocal(dateStr, options = {}) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    const defaultOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      ...options
    };
    // No timeZone specified = use device's local timezone
    return date.toLocaleDateString('en-IN', defaultOptions);
  } catch (e) {
    return '-';
  }
}

/**
 * Format time using DEVICE'S LOCAL TIME
 */
export function formatTimeLocal(dateStr, options = {}) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    const defaultOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    // No timeZone specified = use device's local timezone
    return date.toLocaleTimeString('en-IN', defaultOptions);
  } catch (e) {
    return '-';
  }
}

/**
 * Format date and time using DEVICE'S LOCAL TIME
 */
export function formatDateTimeLocal(dateStr, options = {}) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    const defaultOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    // No timeZone specified = use device's local timezone
    return date.toLocaleString('en-IN', defaultOptions);
  } catch (e) {
    return '-';
  }
}

/**
 * Get current date/time from device (for display like "Today at 3:45 PM")
 */
export function getCurrentDeviceTime() {
  return new Date();
}

/**
 * Format date as relative (Today, Yesterday, or date)
 */
export function formatDateRelative(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (inputDate.getTime() === today.getTime()) {
      return `Today at ${formatTimeLocal(dateStr)}`;
    } else if (inputDate.getTime() === yesterday.getTime()) {
      return `Yesterday at ${formatTimeLocal(dateStr)}`;
    } else {
      return formatDateTimeLocal(dateStr);
    }
  } catch (e) {
    return '-';
  }
}

// Keep old function names as aliases for backward compatibility
// but now they use device local time
export const formatDateIST = formatDateLocal;
export const formatTimeIST = formatTimeLocal;
export const formatDateTimeIST = formatDateTimeLocal;

export function formatCurrencyINR(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
}
